import { Controller } from "@hotwired/stimulus"

// Mantiene al día los <select> de SKU del formulario de orden de compra.
//
// Quien arma el pedido abre el listado de SKUs en otra pestaña para aclarar un
// producto ambiguo ("caldos" con sal vs sin sal) y vuelve esperando ver la
// aclaración. Recargar la página mostraría los nombres nuevos, pero se llevaría
// puestos los renglones ya cargados: la OC nueva todavía no existe en la base.
// Así que al volver a la pestaña se piden las opciones al servidor y se
// reemplazan en su lugar.
//
// Solo se tocan las etiquetas y los data-attributes de las <option>: la
// selección, la cantidad y el precio que se hayan cargado a mano quedan como
// estaban.
export default class extends Controller {
  static values = { url: String }

  connect() {
    this.onWakeUp = () => {
      if (document.visibilityState === "visible") this.refresh()
    }
    // visibilitychange cubre el cambio de pestaña; focus, el cambio de ventana
    // (en algunos navegadores el primero no dispara al alternar ventanas).
    document.addEventListener("visibilitychange", this.onWakeUp)
    window.addEventListener("focus", this.onWakeUp)
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.onWakeUp)
    window.removeEventListener("focus", this.onWakeUp)
  }

  async refresh() {
    if (!this.hasUrlValue || this.refreshing) return

    this.refreshing = true
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!response.ok) return

      this.apply(await response.json())
    } catch (error) {
      // Es una comodidad, no un requisito: si la red falla, el form sigue
      // funcionando con las opciones que ya tenía.
      console.warn("No se pudieron refrescar los SKU:", error)
    } finally {
      this.refreshing = false
    }
  }

  apply(skus) {
    this.element.querySelectorAll('select[data-purchase-order-target="selectedSku"]')
      .forEach((select) => {
        this.replaceOptions(select, skus)
        this.syncNotes(select)
      })

    // Las filas que agrega "Agregar SKU" salen de este <template>: si no se
    // actualiza, nacerían con los nombres viejos.
    this.element.querySelectorAll("template").forEach((template) => {
      template.content.querySelectorAll("select").forEach((select) => {
        this.replaceOptions(select, skus)
      })
    })
  }

  replaceOptions(select, skus) {
    const previous = select.value
    const promptOption = select.querySelector('option[value=""]')
    const selectedOption = select.selectedOptions[0]
    const fragment = document.createDocumentFragment()

    if (promptOption) fragment.appendChild(promptOption.cloneNode(true))

    let selectionSurvives = previous === ""
    skus.forEach((sku) => {
      if (String(sku.id) === previous) selectionSurvives = true
      fragment.appendChild(this.buildOption(sku))
    })

    // Si el SKU elegido dejó de estar en la lista (lo pasaron a oculto mientras
    // se armaba el pedido), se conserva su opción: perder la selección sería
    // peor que mostrar un SKU que ya no se ofrece.
    if (!selectionSurvives && selectedOption) fragment.appendChild(selectedOption.cloneNode(true))

    select.replaceChildren(fragment)
    select.value = previous
  }

  buildOption(sku) {
    const option = document.createElement("option")
    option.value = sku.id
    option.textContent = sku.short_name
    // Los mismos data-attributes que arma supplier_sku_options en el servidor:
    // sku-format alterna short/full y active-sku-details lee precio, IVA y nota.
    option.dataset.shortName = sku.short_name || ""
    option.dataset.fullName = sku.full_name || ""
    option.dataset.measurementUnit = sku.measurement_unit || ""
    option.dataset.measurementFormat = sku.measurement_format || ""
    option.dataset.lastPrice = sku.last_price || ""
    option.dataset.lastPriceInput = sku.last_price_input || ""
    option.dataset.ivaRate = sku.iva_rate || ""
    if (sku.notes) option.dataset.notes = sku.notes

    return option
  }

  // Refresca la nota que se muestra debajo del select, sin disparar un `change`:
  // eso volvería a autocompletar el precio y pisaría el que se haya escrito.
  syncNotes(select) {
    const row = select.closest(".po-item-row")
    if (!row) return

    const details = this.application.getControllerForElementAndIdentifier(row, "active-sku-details")
    details?.updateNotes(select.selectedOptions[0]?.dataset.notes || "")
  }
}
